import axios from "@/widgets/shared/utils/axios-instance";
import Constants from "@/widgets/shared/constants";

export const types = {
  LOAD_RATING: "loadRating",
  LOAD_RATING_SUCCESS: "loadRatingSuccess",
  LOAD_RATING_ERROR: "loadRatingError",
  LOAD_MY_RATING: "loadMyRating",
  LOAD_MY_RATING_SUCCESS: "loadMyRatingSuccess",
  LOAD_MY_RATING_ERROR: "loadMyRatingError",
  SEND_RATING: "sendRating",
  SEND_RATING_SUCCESS: "sendRatingSuccess",
  SEND_RATING_ERROR: "sendRatingError",
};

export const state = () => ({
  myRatingStatuses: {},
  myRatingErrors: {},
  myRatings: {},
  ratingStatuses: {},
  ratingErrors: {},
  ratings: {},
  sendRatingStatus: null,
  sendRatingError: null,
});

export const mutations = {
  [types.LOAD_MY_RATING](state, agendaSessionId) {
    const statuses = {...state.myRatingStatuses};
    statuses[agendaSessionId] = Constants.STATUS_LOADING;
    state.myRatingStatuses = statuses;
  },
  [types.LOAD_MY_RATING_SUCCESS](state, agendaSessionId) {
    const statuses = {...state.myRatingStatuses};
    statuses[agendaSessionId] = Constants.STATUS_LOADED;
    state.myRatingStatuses = statuses;
  },
  [types.LOAD_MY_RATING_ERROR](state, {agendaSessionId, error}) {
    const statuses = {...state.myRatingStatuses};
    const errors = {...state.myRatingErrors};
    errors[agendaSessionId] = error;
    statuses[agendaSessionId] = Constants.STATUS_ERROR;
    state.myRatingStatuses = statuses;
    state.myRatingErrors = errors;
  },
  setMyRating(state, {agendaSessionId, myRating}) {
    const myRatings = {...state.myRatings};
    myRatings[agendaSessionId] = myRating;
    state.myRatings = myRatings;
  },

  [types.LOAD_RATING](state, agendaSessionId) {
    const statuses = {...state.ratingStatuses};
    statuses[agendaSessionId] = Constants.STATUS_LOADING;
    state.ratingStatuses = statuses;
  },
  [types.LOAD_RATING_SUCCESS](state, agendaSessionId) {
    const statuses = {...state.ratingStatuses};
    statuses[agendaSessionId] = Constants.STATUS_LOADED;
    state.ratingStatuses = statuses;
  },
  [types.LOAD_RATING_ERROR](state, {agendaSessionId, error}) {
    const statuses = {...state.ratingStatuses};
    const errors = {...state.ratingErrors};
    errors[agendaSessionId] = error;
    statuses[agendaSessionId] = Constants.STATUS_ERROR;
    state.ratingStatuses = statuses;
    state.ratingErrors = errors;
  },
  setRating(state, {agendaSessionId, rating}) {
    const ratings = {...state.ratings};
    ratings[agendaSessionId] = rating;
    state.ratings = ratings;
  },
  clearRatingError(state, agendaSessionId) {
    const errors = {...state.ratingErrors};
    delete errors[agendaSessionId];
    state.ratingErrors = errors;
  },
  [types.SEND_RATING](state) {
    state.sendRatingStatus = Constants.STATUS_LOADING;
  },
  [types.SEND_RATING_SUCCESS](state) {
    state.sendRatingStatus = Constants.STATUS_LOADED;
  },
  [types.SEND_RATING_ERROR](state, error) {
    state.sendRatingError = error;
    state.sendRatingStatus = Constants.STATUS_ERROR;
  },
};

export const actions = {
  async fetchMyRating({commit, getters, rootState, state}, {componentId, agendaSessionId}) {
    if (!getters.getIsLoadingMyRating(agendaSessionId)) {
      const eventId = rootState.eventId;
      commit(types.LOAD_MY_RATING, agendaSessionId);
      return await axios
        .get(`events/${eventId}/components/${componentId}/agenda_sessions/${agendaSessionId}/my`)
        .then(result => {
          commit("setMyRating", {agendaSessionId: agendaSessionId, myRating: result.data});
          commit(types.LOAD_MY_RATING_SUCCESS, agendaSessionId);
        })
        .catch(error => {
          commit(types.LOAD_MY_RATING_ERROR, {agendaSessionId, error});
        });
    } else {
      return Promise.resolve();
    }
  },

  async fetchRating({commit, getters, rootState, state}, {componentId, agendaSessionId}) {
    if (!getters.getIsLoadingRating(agendaSessionId)) {
      const eventId = rootState.eventId;
      commit(types.LOAD_RATING, agendaSessionId);
      return await axios
        .get(`events/${eventId}/components/${componentId}/agenda_sessions/${agendaSessionId}/rating`)
        .then(result => {
          commit("setRating", {agendaSessionId: agendaSessionId, rating: result.data.ratings});
          commit(types.LOAD_RATING_SUCCESS, agendaSessionId);
        })
        .catch(error => {
          commit(types.LOAD_RATING_ERROR, {agendaSessionId, error});
        });
    } else {
      return Promise.resolve();
    }
  },

  async sendRating({commit, getters, rootState, state, dispatch}, {componentId, agendaSessionId, responses}) {
    const body = mapResponsesToRequestBody(responses);
    if (!getters.isLoading) {
      const eventId = rootState.eventId;
      commit(types.SEND_RATING);
      return await axios
        .post(`events/${eventId}/components/${componentId}/agenda_sessions/${agendaSessionId}/rating`, body)
        .then(async (result) => {
          await commit("setMyRating", {agendaSessionId: agendaSessionId, myRating: result.data});
          await commit(types.SEND_RATING_SUCCESS);
          await dispatch("fetchRating", {componentId: componentId, agendaSessionId: agendaSessionId});
        })
        .catch(error => {
          commit(types.SEND_RATING_ERROR, error);
        });
    } else {
      return Promise.resolve();
    }
  },
};

export const getters = {
  getIsLoadingMyRating: state => agendaSessionId => state.myRatingStatuses[agendaSessionId] === Constants.STATUS_LOADING,

  getMyRating: state => agendaSessionId => state.myRatings[agendaSessionId],

  getIsLoadingRating: state => agendaSessionId => state.ratingStatuses[agendaSessionId] === Constants.STATUS_LOADING,

  getRating: state => agendaSessionId => state.ratings[agendaSessionId],

  isSending: state => state.sendRatingStatus === Constants.STATUS_LOADING,

  getRatingError: state => agendaSessionId => state.ratingErrors[agendaSessionId],
};

function mapResponsesToRequestBody(responses) {
  const mappedResponses = Object.entries(responses)
    .map(responseEntry => {
      if (responseEntry[1].type === Constants.AGENDA_SESSION_RATING_TYPES.OPEN) {
        return {
          agenda_session_rating_id: Number.parseInt(responseEntry[0]),
          open_question_answer: responseEntry[1].responses,
        };
      } else {
        return {
          agenda_session_rating_id: Number.parseInt(responseEntry[0]),
          agenda_session_answer_ids: responseEntry[1].responses,
        };
      }
    })
    .filter(response => {
      return (response.agenda_session_answer_ids && response.agenda_session_answer_ids.length) || response.open_question_answer;
    });

  return {responses: mappedResponses};
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
